'use client'

import React from 'react'
import {
  Eye,
  Monitor,
  Zap,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowRight,
  User,
  Bot,
  Workflow,
} from 'lucide-react'

/**
 * Thread-Autonomy-Trust Map Diagram
 * 2D grid: Thread types (X) × Supervision patterns (Y)
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

const threadTypes = [
  { id: 'B', name: 'Base', color: '#008dcc' },
  { id: 'P', name: 'Parallel', color: '#008dcc' },
  { id: 'L', name: 'Learning', color: '#8b5cf6' },
  { id: 'F', name: 'Fast', color: '#FDC304' },
  { id: 'C', name: 'Creative', color: '#FDC304' },
  { id: 'Z', name: 'Zero-touch', color: '#F90B8A' },
]

const supervisionLevels = [
  {
    id: 'in-loop',
    label: 'In-the-loop',
    description: 'User approves every action',
    icon: User,
    color: '#008dcc',
  },
  {
    id: 'on-loop',
    label: 'On-the-loop',
    description: 'User monitors, intervenes when needed',
    icon: Monitor,
    color: '#8b5cf6',
  },
  {
    id: 'guided',
    label: 'Guided autonomy',
    description: 'Agent acts within defined scope',
    icon: Workflow,
    color: '#FDC304',
  },
  {
    id: 'out-loop',
    label: 'Out-of-the-loop',
    description: 'Agent acts independently',
    icon: Bot,
    color: '#F90B8A',
  },
]

// Which cells are "active" in the 2D grid
const activeMap: Record<string, string[]> = {
  B: ['in-loop'],
  P: ['on-loop'],
  L: ['on-loop'],
  F: ['guided'],
  C: ['guided'],
  Z: ['out-loop'],
}

export function ThreadAutonomyTrustMap() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE THREAD-AUTONOMY-TRUST MAP
        </h3>
        <p className='text-sm text-muted-foreground'>
          Thread types (engineering) mapped to supervision patterns (design)
        </p>
      </div>

      {/* Grid: supervision levels as rows, thread types as columns */}
      <div className='overflow-x-auto'>
        <div className='min-w-[600px]'>
          {/* Header row: thread types */}
          <div className='grid grid-cols-7 gap-1 mb-1'>
            <div className='p-2' /> {/* empty corner */}
            {threadTypes.map(t => (
              <div
                key={t.id}
                className='text-center p-2 rounded-t-lg'
                style={{ backgroundColor: `${t.color}15` }}
              >
                <div className='text-sm font-bold' style={{ color: t.color }}>
                  {t.id}
                </div>
                <div className='text-[10px] text-muted-foreground'>
                  {t.name}
                </div>
              </div>
            ))}
          </div>

          {/* Data rows: supervision levels */}
          {supervisionLevels.map(level => (
            <div key={level.id} className='grid grid-cols-7 gap-1 mb-1'>
              {/* Row label */}
              <div
                className='flex items-center gap-1.5 p-2 rounded-l-lg'
                style={{ backgroundColor: `${level.color}10` }}
              >
                <level.icon
                  className='w-3.5 h-3.5 flex-shrink-0'
                  style={{ color: level.color }}
                />
                <div>
                  <div
                    className='text-xs font-semibold leading-tight'
                    style={{ color: level.color }}
                  >
                    {level.label}
                  </div>
                  <div className='text-[9px] text-muted-foreground leading-tight hidden md:block'>
                    {level.description}
                  </div>
                </div>
              </div>

              {/* Cells */}
              {threadTypes.map(t => {
                const isActive = activeMap[t.id]?.includes(level.id)
                return (
                  <div
                    key={`${t.id}-${level.id}`}
                    className='flex items-center justify-center p-2 rounded-sm'
                    style={{
                      backgroundColor: isActive
                        ? `${t.color}20`
                        : 'transparent',
                    }}
                  >
                    {isActive && (
                      <div
                        className='w-6 h-6 rounded-full flex items-center justify-center'
                        style={{ backgroundColor: `${t.color}30` }}
                      >
                        <CheckCircle
                          className='w-4 h-4'
                          style={{ color: t.color }}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend: autonomy progression arrow */}
      <div className='mt-6 flex items-center justify-center gap-2'>
        <div className='flex items-center gap-1.5'>
          <Shield className='w-3.5 h-3.5' style={{ color: '#008dcc' }} />
          <span className='text-xs text-muted-foreground'>Full control</span>
        </div>
        <ArrowRight className='w-4 h-4 text-muted-foreground' />
        <div className='flex items-center gap-1.5'>
          <Eye className='w-3.5 h-3.5' style={{ color: '#8b5cf6' }} />
          <span className='text-xs text-muted-foreground'>Monitored</span>
        </div>
        <ArrowRight className='w-4 h-4 text-muted-foreground' />
        <div className='flex items-center gap-1.5'>
          <Workflow className='w-3.5 h-3.5' style={{ color: '#FDC304' }} />
          <span className='text-xs text-muted-foreground'>Guided</span>
        </div>
        <ArrowRight className='w-4 h-4 text-muted-foreground' />
        <div className='flex items-center gap-1.5'>
          <Zap className='w-3.5 h-3.5' style={{ color: '#F90B8A' }} />
          <span className='text-xs text-muted-foreground'>Autonomous</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Shadow-to-Steer-to-Self progression diagram
 * Shows the migration from B-thread to Z-thread with UI/governance evolution
 */
export function ShadowToSteerToSelfDiagram() {
  const stages = [
    {
      phase: 'Shadow',
      threads: 'B-thread',
      color: '#008dcc',
      governance: 'Human approves every action',
      ux: 'Approval dialogs, action previews',
      trust: 'Agent proves competence',
      icon: User,
    },
    {
      phase: 'Steer',
      threads: 'P/L-thread',
      color: '#8b5cf6',
      governance: 'Monitoring with alert thresholds',
      ux: 'Dashboard, status indicators, intervention controls',
      trust: 'User learns to monitor instead of approve',
      icon: Monitor,
    },
    {
      phase: 'Guide',
      threads: 'F/C-thread',
      color: '#FDC304',
      governance: 'Pre-approved scope, review gates for edge cases',
      ux: 'Scope boundaries visible, periodic summaries, drafts for review',
      trust: 'User defines boundaries, agent operates within them',
      icon: Workflow,
    },
    {
      phase: 'Self',
      threads: 'Z-thread',
      color: '#F90B8A',
      governance: 'Full governance rails, rollback, audit logs',
      ux: 'Outcome reports, exception alerts only',
      trust: 'User reviews outcomes, not actions',
      icon: Bot,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SHADOW TO STEER TO SELF
        </h3>
        <p className='text-sm text-muted-foreground'>
          The migration pattern from full human control to autonomous operation
        </p>
      </div>

      <div className='space-y-3'>
        {stages.map((stage, i) => (
          <div key={stage.phase}>
            <div className='bg-background border border-border/60 rounded-xl p-4 md:p-5'>
              {/* Phase header */}
              <div className='flex items-center gap-3 mb-3'>
                <div
                  className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${stage.color}20` }}
                >
                  <stage.icon
                    className='w-4.5 h-4.5'
                    style={{ color: stage.color }}
                  />
                </div>
                <div>
                  <div className='flex items-center gap-2'>
                    <span
                      className='text-sm font-bold'
                      style={{ color: stage.color }}
                    >
                      {stage.phase}
                    </span>
                    <span
                      className='text-[10px] px-2 py-0.5 rounded-full font-medium'
                      style={{
                        backgroundColor: `${stage.color}15`,
                        color: stage.color,
                      }}
                    >
                      {stage.threads}
                    </span>
                  </div>
                </div>
              </div>

              {/* Three columns */}
              <div className='grid md:grid-cols-3 gap-2'>
                <div className='bg-muted rounded-lg p-2.5'>
                  <div className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1'>
                    Governance
                  </div>
                  <div className='text-xs text-foreground/80'>
                    {stage.governance}
                  </div>
                </div>
                <div className='bg-muted rounded-lg p-2.5'>
                  <div className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1'>
                    User Experience
                  </div>
                  <div className='text-xs text-foreground/80'>{stage.ux}</div>
                </div>
                <div className='bg-muted rounded-lg p-2.5'>
                  <div className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1'>
                    Trust Signal
                  </div>
                  <div className='text-xs text-foreground/80'>
                    {stage.trust}
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow between stages */}
            {i < stages.length - 1 && (
              <div className='flex justify-center py-1'>
                <div className='flex flex-col items-center'>
                  <div className='w-px h-3 bg-[var(--border)]' />
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Named Patterns and Anti-Patterns
 */
export function PatternsAntiPatternsDiagram() {
  const patterns = [
    {
      name: 'Audit-Ready Z-Thread',
      description:
        'Autonomous agent with comprehensive logging, explainability surfaces, and one-click rollback. Every action is auditable.',
      thread: 'Z',
      color: '#F90B8A',
    },
    {
      name: 'Shadow-to-Steer-to-Self',
      description:
        'Migration pattern: start with shadow agents (B-thread, in-loop), gather performance data, progressively upgrade to Z-thread.',
      thread: 'B→Z',
      color: '#8b5cf6',
    },
    {
      name: 'C-Thread Review Lane',
      description:
        'Creative agent generates multiple options. User reviews and selects from drafts. Quality gate ensures options meet minimum bar.',
      thread: 'C',
      color: '#FDC304',
    },
    {
      name: 'Exception-Only Escalation',
      description:
        'Z-thread agent handles normal operations silently. Only surfaces to users when anomaly thresholds are breached.',
      thread: 'Z',
      color: '#F90B8A',
    },
    {
      name: 'Thread SLA Contract',
      description:
        'For each thread type, define max autonomy, verification method, observable UX, escalation path, and rollback procedure.',
      thread: 'All',
      color: '#008dcc',
    },
  ]

  const antiPatterns = [
    {
      name: 'Invisible Autonomy',
      description:
        'Agent acts autonomously but the UI shows no indication of what it did or why. Users lose trust because they cannot verify.',
    },
    {
      name: 'Governance Theater',
      description:
        'UI shows trust signals (confidence scores, check marks) that are not backed by real engineering verification.',
    },
    {
      name: 'Permanent B-Thread',
      description:
        'Every interaction requires human approval forever. No path to upgrade autonomy even after thousands of successful completions.',
    },
    {
      name: 'Opaque Z-Thread',
      description:
        'Fully autonomous agent with no audit trail, no explainability, and no rollback. One failure and all trust is destroyed.',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          PATTERNS AND ANTI-PATTERNS
        </h3>
        <p className='text-sm text-muted-foreground'>
          Named patterns to follow and anti-patterns to avoid
        </p>
      </div>

      {/* Patterns */}
      <div className='mb-5'>
        <div className='flex items-center gap-2 mb-3'>
          <CheckCircle className='w-4 h-4' style={{ color: '#22c55e' }} />
          <span className='text-sm font-semibold text-foreground'>
            Patterns
          </span>
        </div>
        <div className='space-y-2'>
          {patterns.map(p => (
            <div
              key={p.name}
              className='bg-background border border-border/60 rounded-lg p-3 flex items-start gap-3'
            >
              <span
                className='text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 mt-0.5'
                style={{
                  backgroundColor: `${p.color}15`,
                  color: p.color,
                }}
              >
                {p.thread}
              </span>
              <div>
                <div className='text-sm font-semibold text-foreground'>
                  {p.name}
                </div>
                <div className='text-xs text-muted-foreground'>
                  {p.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Anti-patterns */}
      <div>
        <div className='flex items-center gap-2 mb-3'>
          <XCircle className='w-4 h-4' style={{ color: '#ef4444' }} />
          <span className='text-sm font-semibold text-foreground'>
            Anti-Patterns
          </span>
        </div>
        <div className='space-y-2'>
          {antiPatterns.map(ap => (
            <div
              key={ap.name}
              className='bg-background border border-border/60 rounded-lg p-3 flex items-start gap-3'
            >
              <AlertTriangle
                className='w-4 h-4 flex-shrink-0 mt-0.5'
                style={{ color: '#ef4444' }}
              />
              <div>
                <div className='text-sm font-semibold text-foreground'>
                  {ap.name}
                </div>
                <div className='text-xs text-muted-foreground'>
                  {ap.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
